import { useTranslation } from 'react-i18next'
import { CalendarDays, CalendarRange, Inbox, NotebookText, User } from 'lucide-react'
import { StatusRing } from './AuthLoginPreview'
import { cn } from '@/lib/utils'

export type OnboardingPreviewStage = 'profile' | 'workspace' | 'invite' | 'tasks'

interface OnboardingPreviewProps {
  stage: OnboardingPreviewStage
  name: string
  photoUrl: string | null
  mode: 'personal' | 'team'
  workspaceName: string
  invitees: Array<string>
  tasks: Array<string>
}

/** First letters of the first two words, e.g. "Jamie Rivera" → "JR". */
export function getInitials(value: string): string {
  const parts = value.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  const first = parts[0]?.charAt(0) ?? ''
  const second = parts.length > 1 ? (parts.at(-1)?.charAt(0) ?? '') : ''
  return (first + second).toUpperCase()
}

export function UserAvatar({
  name,
  photoUrl,
  className,
}: {
  name: string
  photoUrl: string | null
  className?: string
}) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand font-semibold text-brand-foreground',
        className,
      )}
    >
      {photoUrl ? (
        <img src={photoUrl} alt="" className="size-full object-cover" />
      ) : (
        getInitials(name) || <User aria-hidden className="size-[45%]" />
      )}
    </span>
  )
}

/**
 * Live, miniature version of the app shown beside the onboarding wizard
 * (design turn 14). Name, avatar, workspace name, invitees and tasks update
 * as the user types, so each step shows what it's about to create.
 */
export function OnboardingPreview({
  stage,
  name,
  photoUrl,
  mode,
  workspaceName,
  invitees,
  tasks,
}: OnboardingPreviewProps) {
  const { t } = useTranslation('auth')
  const trimmedWorkspace = workspaceName.trim()
  const isTeam = mode === 'team' && stage !== 'profile'
  const displayName = name.trim() || t('onboarding.preview.you')
  const filledTasks = tasks.map((task) => task.trim()).filter(Boolean)

  const headline = isTeam
    ? trimmedWorkspace
      ? t('onboarding.preview.team', { name: trimmedWorkspace })
      : t('onboarding.preview.teamUnnamed')
    : t('onboarding.preview.settingUp')

  const workspaceLabel = (isTeam && trimmedWorkspace) || t('onboarding.preview.workspace')

  const nav = [
    { icon: Inbox, label: t('onboarding.preview.inbox'), active: true },
    { icon: CalendarDays, label: t('onboarding.preview.today') },
    { icon: CalendarRange, label: t('onboarding.preview.next7') },
    { icon: NotebookText, label: t('onboarding.preview.notes') },
  ]

  return (
    <div className="flex h-full flex-col justify-center gap-5 p-10 xl:p-14">
      <p className="flex items-center gap-2 text-sm font-medium text-brand-tint-foreground">
        <span className="size-2 rounded-full bg-brand" />
        {headline}
      </p>

      <div className="flex h-[420px] w-full max-w-[600px] overflow-hidden rounded-xl bg-card text-card-foreground shadow-[0_1px_2px_rgba(0,0,0,0.04),0_16px_40px_-12px_rgba(90,40,20,0.3)]">
        {/* Mini sidebar */}
        <div className="flex w-[180px] shrink-0 flex-col bg-chrome p-3">
          <div className="flex items-center gap-2 px-1.5 py-1">
            <span className="flex size-5 items-center justify-center rounded-md bg-foreground text-[10px] font-semibold text-background">
              {workspaceLabel.charAt(0).toUpperCase()}
            </span>
            <span className="truncate text-[13px] font-semibold">{workspaceLabel}</span>
          </div>

          <div className="mt-4 space-y-0.5">
            {nav.map(({ icon: Icon, label, active }) => (
              <div
                key={label}
                className={cn(
                  'flex items-center gap-2 rounded-md px-1.5 py-1 text-[12px]',
                  active ? 'bg-card font-medium text-foreground shadow-xs' : 'text-muted-foreground',
                )}
              >
                <Icon className="size-3.5" />
                <span className="truncate">{label}</span>
                {active && filledTasks.length > 0 ? (
                  <span className="ml-auto text-[11px] font-medium text-brand">{filledTasks.length}</span>
                ) : null}
              </div>
            ))}
          </div>

          {isTeam ? (
            <div className="mt-5">
              <p className="px-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                {t('onboarding.preview.members')}
              </p>
              <div className="mt-1.5 space-y-1">
                <div className="flex items-center gap-2 px-1.5 py-0.5 text-[12px]">
                  <UserAvatar name={displayName} photoUrl={photoUrl} className="size-4 text-[8px]" />
                  <span className="truncate">{displayName}</span>
                </div>
                {invitees.slice(0, 3).map((email) => (
                  <div key={email} className="flex items-center gap-2 px-1.5 py-0.5 text-[12px] text-muted-foreground">
                    <span className="flex size-4 items-center justify-center rounded-full border border-dashed border-muted-foreground/60 text-[8px] uppercase">
                      {email.charAt(0)}
                    </span>
                    <span className="truncate">{email}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div
            className={cn(
              'mt-auto flex items-center gap-2 rounded-lg px-1.5 py-1.5 transition-shadow',
              stage === 'profile' && 'bg-card ring-2 ring-brand',
            )}
          >
            <UserAvatar name={displayName} photoUrl={photoUrl} className="size-6 text-[10px]" />
            <span className="truncate text-[12px] font-medium">{displayName}</span>
          </div>
        </div>

        {/* Inbox pane */}
        <div className="flex min-w-0 flex-1 flex-col p-5">
          <p className="text-[15px] font-semibold">{t('onboarding.preview.inbox')}</p>
          <div className="mt-3 flex-1">
            {filledTasks.length > 0
              ? filledTasks.map((task, index) => (
                  <div key={`${index}-${task}`} className="flex items-center gap-2.5 border-b border-border/70 py-2 text-[13px]">
                    <StatusRing />
                    <span className="truncate">{task}</span>
                  </div>
                ))
              : [72, 56, 64, 44].map((width) => (
                  <div key={width} className="flex items-center gap-2.5 border-b border-border/70 py-2.5">
                    <span className="size-3.5 shrink-0 rounded-full border-[1.5px] border-border" />
                    <span className="h-2 rounded-full bg-secondary" style={{ width: `${width}%` }} />
                  </div>
                ))}
          </div>
        </div>
      </div>

      <p className={cn('text-sm text-brand-tint-foreground', stage === 'tasks' && filledTasks.length > 0 ? '' : 'invisible')}>
        {t('onboarding.preview.tasksReady', { count: filledTasks.length })}
      </p>
    </div>
  )
}
